import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useAuth } from "../../hooks";
import { motion } from "framer-motion";
import { SignInMainContainer } from "./SignInDesign";
import { useUpdateUsername } from "../../services/api/user.api";

const validationSchema = Yup.object({
  username: Yup.string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be less than 20 characters")
    .required("Username is required"),
});

const AddUsernamePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { userInfo, setUser } = useAuth();
  const { update, isLoading } = useUpdateUsername({ key: ["updateUsername"] });

  // Get the redirect path from location state or default to user dashboard
  const redirectPath = location.state?.redirect || `/user/${userInfo?.id}`;

  const formik = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await update(
          {
            username: values.username,
          },
          {
            onSuccess: (data) => {
              if (data.data) {
                setUser(data.data);
              }
              enqueueSnackbar("Username updated successfully", {
                variant: "success",
              });
              navigate(redirectPath);
            },
            onError: (error: any) => {
              enqueueSnackbar(
                error?.response?.data?.message || "Failed to update username",
                { variant: "error" }
              );
            },
          }
        );
      } catch (error: any) {
        console.log(error?.response?.data?.message);
      }
    },
  });

  return (
    <SignInMainContainer
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100vh",
      }}
    >
      {!isMobile && (
        <Box
          flex={1}
          sx={{
            position: "relative",
            backgroundColor: "#F5F5FA",
            borderRadius: { xs: 0, md: "24px 0 0 24px" },
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 4, md: 8 },
            textAlign: { xs: "center", md: "left" },
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-20%",
              left: "-20%",
              width: 500,
              height: 500,
              background:
                "radial-gradient(circle, #9F03FF33 0%, transparent 70%)",
              filter: "blur(60px)",
              zIndex: 0,
            },
          }}
        >
          <Stack
            spacing={4}
            maxWidth={460}
            sx={{ position: "relative", zIndex: 1 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Typography
                variant="h4"
                fontFamily="Lexend"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(90deg, #6366F1, #9F03FF)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: 1.2,
                }}
              >
                StageLink
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontFamily="Lexend"
                fontWeight={700}
                lineHeight={1.4}
                sx={{ textAlign: { xs: "center", md: "left" } }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    background: "linear-gradient(90deg, #6366F1, #3B82F6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Empowering Passion.
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    mt: { xs: 1, md: 0.5 },
                    background: "linear-gradient(90deg, #A044FF, #FF6BFF)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Connecting the World.
                </Box>
              </Typography>
            </motion.div>

            {!isMobile && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Typography
                  variant="body1"
                  fontFamily="Lexend"
                  sx={{
                    color: "#4D4D4D",
                    opacity: 0.85,
                    fontSize: { xs: "0.95rem", md: "1rem" },
                  }}
                >
                  Experience real-time, authentic moments with global creators —
                  all on one seamless, intuitive platform.
                </Typography>
              </motion.div>
            )}
          </Stack>
        </Box>
      )}

      <Box
        flex={1}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 4, md: 8 },
          bgcolor: "#FDFDFE",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 550 }}
        >
          <Paper
            elevation={4}
            sx={{
              p: { xs: 4, sm: 6, md: 8 },
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            {/* Header */}
            <Typography
              fontFamily="Lexend"
              fontWeight={800}
              variant={isMobile ? "h4" : "h3"}
              mb={1}
              sx={{
                background: "linear-gradient(90deg,#6366F1 0%,#9F03FF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              StageLink
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              mb={1}
              fontFamily="Lexend"
              fontWeight={600}
              color="text.primary"
            >
              Choose Your Username
            </Typography>

            <Typography
              variant="body1"
              mb={4}
              color="text.secondary"
              fontFamily="Lexend"
            >
              Pick a unique username that represents you on StageLink
            </Typography>

            {/* Form */}
            <Box component="form" onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                name="username"
                label="Username"
                placeholder="Enter your username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant="body1"
                      sx={{ mr: 1, color: "text.secondary" }}
                    >
                      @
                    </Typography>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={
                  isLoading || !formik.values.username || !formik.isValid
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: "linear-gradient(90deg,#6366F1 0%,#9F03FF 100%)",
                  fontFamily: "Lexend",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textTransform: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg,#5B5BD6 0%,#8F02E6 100%)",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Continue"
                )}
              </Button>
            </Box>

            {/* Tips */}
            <Box sx={{ mt: 4, textAlign: "left" }}>
              <Typography
                variant="subtitle2"
                fontFamily="Lexend"
                fontWeight={600}
                mb={2}
                color="text.primary"
              >
                Username Guidelines:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  fontFamily="Lexend"
                  sx={{ mb: 0.5 }}
                >
                  3-20 characters long
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  fontFamily="Lexend"
                  sx={{ mb: 0.5 }}
                >
                  Letters, numbers, and underscores only
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  fontFamily="Lexend"
                  sx={{ mb: 0.5 }}
                >
                  Must be unique across the platform
                </Typography>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </SignInMainContainer>
  );
};

export default AddUsernamePage;
